import React, { useCallback, useEffect, useState } from 'react';
import { GlobalFilter } from 'components/atoms/tables/smarttables/Filters';
import useCachedState from 'components/atoms/tables/smarttables/helpers/useCachedState';
import styled from 'styled-components';
import { Button, Dropdown, Form, Header, Popup, Input } from 'semantic-ui-react';
import { useForm } from 'react-hook-form-6/dist/index.ie11';
import { find, get, uniq } from 'lodash';
import { filters, filterOptionSets } from 'components/atoms/tables/smarttables/helpers/filters';
import { formatRow } from 'components/atoms/tables/smarttables/helpers/helper';
import { PopupContainer } from 'components/atoms/tables/smarttables/layout/flex';
import { DataPills } from '../CommonAssets';

const Container = styled.div`
    display: block;
    flex-direction: column;
    float:left;
    overflow: visible;
`;

const FilterContainer = styled.div`
    display: flex;
    width: 50%;
`;

const columnOptions = columns => columns.map(x => ({
    key: x.id,
    text: typeof x.Header === 'string'
        ? x.Header
        : x.id,
    value: x.id,
}));

const buildColumnData = (columns, prepareRow) => columns.reduce((acc, column) => {
    const { id, filter, filteredRows } = column;
    const uniqueValues = uniq(
        filteredRows
            .filter((row) => {
                const val = row.values[id];
                return val !== null && val !== undefined;
            })
            .map(row => formatRow(row, 'formatFilterValue', prepareRow)[id])
    );
    acc[id] = {
        filters: filterOptionSets[filter],
        valueOptions: uniqueValues.map(x => ({ key: x, text: `${x}`, value: x })),
    };
    return acc;
}, {});

const ColumnFilters = ({
    controlledSearch,
    updateSearch,
    defaultFilters,
    globalSearchPlaceholder,
    cacheKey,
    isReady = () => null,
    tableProps,
}) => {
    const {
        allColumns,
        preGlobalFilteredRows,
        setAllFilters,
        prepareRow,
        state: {
            filters: filtersApplied,
            globalFilter
        }
    } = tableProps;
    const [filterConfig, setFilterConfig] = useCachedState(`${cacheKey}-table-filter-config`, []);
    const [popupOpen, setPopupOpen] = useState(false);
    const setFilterInner = useCallback((id, value) => {
        if (cacheKey) {
            setFilterConfig([...filtersApplied, { id, value }]);
        }
        setAllFilters([...filtersApplied, { id, value }]);
    }, [cacheKey, setAllFilters, setFilterConfig]);

    const removeFilter = useCallback(({ value }) => {
        if (cacheKey) {
            setFilterConfig(f => f.filter(({ value: innerValue }) => !(
                innerValue.column === value.column &&
                innerValue.value === value.value &&
                innerValue.filter === value.filter
            )));
        }
        setAllFilters(filtersApplied.filter(({ value: innerValue }) => !(
            innerValue.column === value.column &&
            innerValue.value === value.value &&
            innerValue.filter === value.filter
        )));
    }, [cacheKey, setAllFilters, setFilterConfig]);

    useEffect(() => {
        if (cacheKey && filterConfig.length) {
            const columnIds = allColumns.map(column => column.id);
            const newConfig = filterConfig.reduce((newFilterConfig, columnFilter) => {
                if (columnIds.includes(columnFilter.id)) {
                    newFilterConfig.push({
                        id: columnFilter.id,
                        value: {
                            ...columnFilter.value,
                            fn: get(filters[columnFilter.value.raw], 'fn', null)
                        }
                    });
                }
                return newFilterConfig;
            }, []);
            setAllFilters([...filtersApplied, ...newConfig]);
        } else if (defaultFilters && defaultFilters.length) {
            defaultFilters.forEach((newFilter) => {
                const filter = filters[newFilter.filter];
                const filterValue = {
                    column: get(find(allColumns, { id: newFilter.columnId }), 'Header', ''),
                    filter: get(filter, 'label', ''),
                    value: get(newFilter, 'value', null),
                    fn: get(filters[newFilter.filter], 'fn', null),
                    raw: newFilter.filter
                };

                setFilterInner(newFilter.columnId, filterValue);
            });
        }

        // gives the table view enough time to now show column flicker from the above toggle.
        setTimeout(() => {
            isReady();
        }, 100);
        // eslint-disable-next-line
    }, []);

    const { handleSubmit, register, setValue, watch, } = useForm({ mode: 'all' });
    const column = watch('columnId');
    const allColumnsWatched = watch(['columnId', 'filter', 'value']);
    const checkValid = val => val !== undefined && val !== null;
    const isValid = checkValid(allColumnsWatched.columnId)
    && checkValid(allColumnsWatched.filter) &&
    checkValid(allColumnsWatched.value);

    const onSubmit = (data) => {
        const filter = filters[data.filter];

        let columnName = get(find(allColumns, { id: data.columnId }), 'Header', '');
        if (typeof columnName !== 'string') {
            columnName = data.columnId;
        }

        const filterValue = {
            column: columnName,
            filter: get(filter, 'label', ''),
            value: get(data, 'value', null),
            fn: get(filter, 'fn', null),
            raw: data.filter
        };
        setFilterInner(data.columnId, filterValue);
        setPopupOpen(false);
    };

    useEffect(() => {
        register({ name: 'columnId', required: true });
        register({ name: 'filter', required: true });
        register({ name: 'value', required: true });
    }, [register]);

    const [columnData, setColumnData] = useState({});
    const [options, setOptions] = useState([]);
    useEffect(() => {
        setColumnData(buildColumnData(allColumns, prepareRow));
    }, [allColumns, prepareRow]);
    useEffect(() => {
        setOptions(get(columnData[column], 'valueOptions', []));
    }, [columnData, column]);

    return (
        <Container>
            <FilterContainer>
                <GlobalFilter
                    width="90%"
                    minwidth="300px"
                    maxwidth="500px"
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={controlledSearch || globalFilter}
                    setGlobalFilter={updateSearch}
                    placeholder={globalSearchPlaceholder}
                />
                <Popup
                    flowing
                    basic
                    position='bottom center'
                    on='click'
                    trigger={<Button style={{ marginLeft: '5px' }} basic content='Filter' icon='filter' labelPosition='right'/>}
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                    onOpen={() => setPopupOpen(true)}
                >
                    <PopupContainer>
                        <Header as='h2'>Column Filters</Header>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Form.Group widths='equal'>
                                <Form.Field>
                                    <Dropdown
                                        id='columnId'
                                        name='columnId'
                                        search
                                        selection
                                        placeholder='Column'
                                        options={columnOptions(allColumns.filter(x => !x.disableGlobalFilter))}
                                        onChange={(e, { name, value }) => setValue(name, value)}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <Dropdown
                                        id='filter'
                                        name='filter'
                                        search
                                        selection
                                        placeholder='Filter'
                                        options={get(columnData[column], 'filters', [])}
                                        onChange={(e, { name, value }) => setValue(name, value)}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    {['contains', 'doesNotContain', 'sw', 'ew'].includes(allColumnsWatched.filter)
                                        ? <Input
                                            id='value'
                                            name='value'
                                            placeholder='Value'
                                            onChange={(e, { name, value }) => setValue(name, value)}
                                        />
                                        : <Dropdown
                                            id='value'
                                            name='value'
                                            search={!['eq', 'doesNotEq'].includes(allColumnsWatched.filter)}
                                            selection
                                            allowAdditions
                                            additionLabel=''
                                            placeholder='Value'
                                            options={options}
                                            onAddItem={(e, { value }) => setOptions((prev) => {
                                                prev.push({ key: value, text: value, value });
                                                return prev;
                                            })}
                                            onChange={(e, { name, value }) => setValue(name, value)}
                                        />}
                                </Form.Field>
                                <Button disabled={!isValid} type='submit'>Apply</Button>
                            </Form.Group>
                        </Form>
                    </PopupContainer>
                </Popup>
            </FilterContainer>
            <DataPills filtersApplied={filtersApplied} removeFilter={removeFilter}/>
        </Container>
    );
};

export default ColumnFilters;



// WEBPACK FOOTER //
// ./src/components/atoms/tables/smarttables/tablePlugins/ColumnFilters.js